import { Flex, Stack, StackDivider, Text } from "@chakra-ui/react";
import type { FlexProps } from "@chakra-ui/react";
import BaseAccordion from "@/components/Accordion/Accordion/BaseAccordion";
import BaseAccordionItem from "@/components/Accordion/AccordionItem/BaseAccordionItem";
import { ContentBlock } from "@/components/ContentBlock/ContentBlock";
import { MetricRatings } from "@/components/DefinitionList/MetricRatings/MetricRatings";
import { Disclaimer } from "@/components/Disclaimer/Disclaimer";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { htmlify, interpolatePlaceholders } from "@/services/utils";
import type { FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";

export interface FxMedalistRatingPanelProps extends FlexProps {
	initiallyExpanded: true;
	heading: string;
	asOf: string;
	asOfDateTitle: string;
	items: {
		title: string;
		value: {
			ratingValue: string;
			image: string;
		};
	}[];
	ratings: { ratingTitle: string; percentage: number }[];
	disclaimer: string;
}

export const FxMedalistRatingPanel = ({ dashboard }: FundDetailLayoutComponentProps) => {
	const instanceConfig = useInstanceConfig();
	const { fxMedalistRatingPanel: props } = dashboard;
	if (!props) {
		return null;
	}

	const { initiallyExpanded, heading, asOf, asOfDateTitle, items, ratings, disclaimer, ...rest } = props;

	return (
		<ContentBlock id="fxMedalistRatingPanel" data-testid="FxMedalistPanel" width="100%" {...rest}>
			<BaseAccordion allowMultiple={true} defaultIndex={initiallyExpanded ? [0] : undefined} variant="dividendInfoDrawer">
				<BaseAccordionItem title={heading} asOfDate={asOf} asOfDateTitle={asOfDateTitle}>
					<Flex py={5} justifyContent="space-between">
						<Flex as="ul" listStyleType="unset" gap={5}>
							{items.map((item, index) => {
								return <MetricRatings as="li" key={index} title={item.title} value={item.value} />;
							})}
						</Flex>
						<Stack as="ul" listStyleType="unset" divider={<StackDivider />} spacing={4} align="stretch">
							{ratings.map((item, index) => {
								return (
									<Flex as="li" key={index} direction="column">
										<Text color="text.heading" variant="content">
											{item.ratingTitle}
										</Text>
										<Text color="text.heading" variant="digit">
											{item.percentage}
										</Text>
									</Flex>
								);
							})}
						</Stack>
					</Flex>
					{Boolean(disclaimer) && (
						<Disclaimer>{htmlify(interpolatePlaceholders({ text: disclaimer, instanceConfig }))}</Disclaimer>
					)}
				</BaseAccordionItem>
			</BaseAccordion>
		</ContentBlock>
	);
};
